import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Alert from "../../components/Alert";
import { listStaff } from "../../api/staffApi";
import { listStaffTimeConfigs, saveStaffTimeConfig, deleteStaffTimeConfig } from "../../api/attendanceApi";
import "../Users/UsersPage.css";

// Direct port of digicampusbd's own day-name/day-index scheme (Saturday-first —
// matches this exact table's real, already-populated production data; see
// AttendanceConfigServiceImpl#dayIndexOf javadoc). The day INDEX itself is
// always computed server-side now — this list only drives the checkbox labels
// and their submitted names.
const DAYS = ["Saturday", "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

/**
 * Direct port of digicampusbd's real "Staff Attendance Time Configuration"
 * screen: one in/delay/out time triplet assigned to a bulk selection of
 * staff x days in a single save (see StaffTimeConfigRequest javadoc — the
 * old app's own request is even shaped as `staffIds: List<Long>`). The
 * previous version of this page only let you pick one staff member and one
 * day at a time — a fidelity regression found live (project owner: "in old
 * project I can select multiple staff"), not a deliberate simplification.
 */
export default function StaffTimeConfigPage() {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({ inTime: "", outTime: "", delayTime: "" });

  useEffect(() => {
    listStaff().then(setStaffList);
  }, []);

  const load = () => {
    setLoading(true);
    listStaffTimeConfigs()
      .then(setConfigs)
      .catch(() => setError("Failed to load time configurations."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggleStaff = (staffId) => {
    setSelectedStaffIds((prev) =>
      prev.includes(staffId) ? prev.filter((id) => id !== staffId) : [...prev, staffId]
    );
  };

  const toggleAllStaff = () => {
    setSelectedStaffIds((prev) => (prev.length === staffList.length ? [] : staffList.map((s) => s.staffId)));
  };

  const toggleDay = (day) => {
    setSelectedDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const toggleAllDays = () => {
    setSelectedDays((prev) => (prev.length === DAYS.length ? [] : [...DAYS]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (selectedStaffIds.length === 0) {
      setError("Select at least one staff member.");
      return;
    }
    if (selectedDays.length === 0) {
      setError("Select at least one day.");
      return;
    }
    setSaving(true);
    try {
      await saveStaffTimeConfig({
        staffIds: selectedStaffIds,
        dayNames: selectedDays,
        inTime: form.inTime,
        outTime: form.outTime,
        delayTime: form.delayTime,
      });
      setSuccess(`Saved for ${selectedStaffIds.length} staff x ${selectedDays.length} day(s).`);
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete time configuration for "${row.staffName}" on "${row.dayName}"?`)) return;
    setError("");
    try {
      await deleteStaffTimeConfig(row.id);
      setSuccess("Deleted.");
      setConfigs((prev) => prev.filter((c) => c.id !== row.id));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  const columns = [
    { key: "staffName", label: "Staff" },
    { key: "dayName", label: "Day" },
    { key: "inTime", label: "In Time" },
    { key: "outTime", label: "Out Time" },
    { key: "delayTime", label: "Delay Time" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Staff Attendance Time Setup</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <form onSubmit={handleSubmit} className="user-form" style={{ marginBottom: 24 }}>
        <label>Staff (select one or more)</label>
        <div className="multi-select-box">
          <label className="multi-select-all">
            <input
              type="checkbox"
              checked={staffList.length > 0 && selectedStaffIds.length === staffList.length}
              onChange={toggleAllStaff}
            />
            Select all
          </label>
          {staffList.map((s) => (
            <label key={s.staffId} className="multi-select-option">
              <input
                type="checkbox"
                checked={selectedStaffIds.includes(s.staffId)}
                onChange={() => toggleStaff(s.staffId)}
              />
              {s.staffName} ({s.customStaffId})
            </label>
          ))}
        </div>

        <label>Days (select one or more)</label>
        <div className="multi-select-box multi-select-box-inline">
          <label className="multi-select-all">
            <input type="checkbox" checked={selectedDays.length === DAYS.length} onChange={toggleAllDays} />
            Select all
          </label>
          {DAYS.map((day) => (
            <label key={day} className="multi-select-option">
              <input type="checkbox" checked={selectedDays.includes(day)} onChange={() => toggleDay(day)} />
              {day}
            </label>
          ))}
        </div>

        <label>In Time</label>
        <input type="time" value={form.inTime} onChange={(e) => setForm({ ...form, inTime: e.target.value })} required />
        <label>Out Time</label>
        <input type="time" value={form.outTime} onChange={(e) => setForm({ ...form, outTime: e.target.value })} required />
        <label>Delay Time (in-time after this counts as "Delay")</label>
        <input type="time" value={form.delayTime} onChange={(e) => setForm({ ...form, delayTime: e.target.value })} />
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : `Save for ${selectedStaffIds.length || 0} staff x ${selectedDays.length || 0} day(s)`}
          </button>
        </div>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={configs}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Delete
            </button>
          )}
        />
      )}
    </MainLayout>
  );
}
